import { TrustJetPlane, getAircraftImage, getAircraftCarouselImages } from '@assets/images';

/**
 * Get the aircraft model name from various possible data structures
 * @param {Object} aircraft - The aircraft object
 * @returns {string} The formatted model name or '-' if not available
 */
export const getAircraftModelName = (aircraft) => {
  if (!aircraft) return '-';

  if (aircraft.aircraft_make_model) {
    const { manufacturer } = aircraft.aircraft_make_model;

    // Extract model number from make_model (e.g., "LR-45-45" -> "45-45")
    let modelNumber = '';
    if (aircraft.make_model) {
      const firstDashIndex = aircraft.make_model.indexOf('-');
      if (firstDashIndex !== -1) {
        modelNumber = aircraft.make_model.substring(firstDashIndex + 1); // Get everything after first dash
      }
    }

    const modelStr = `${manufacturer || ''} ${modelNumber}`.trim();
    return modelStr || '-';
  }

  const fallbackStr = `${aircraft.manufacturer || ''} ${aircraft.make_model || ''}`.trim();
  return fallbackStr || '-';
};

/**
 * Get the aircraft location from operator data
 * @param {Object} operator - The operator object
 * @returns {string} The formatted location (city, state) or empty string if not available
 */
export const getAircraftLocation = (operator) => {
  if (!operator) return '';

  const { faa_city, faa_state } = operator;

  if (!faa_city && !faa_state) return '';

  if (faa_city && faa_state) {
    return `${faa_city}, ${faa_state}`;
  }

  return faa_city || faa_state || '';
};

/**
 * Get the operator name with location
 * @param {Object} operator - The operator object
 * @returns {string} The formatted operator name with location (e.g., "Operator Name • City, State")
 */
export const getOperatorWithLocation = (operator) => {
  if (!operator) return '-';

  const name = operator.name || '-';
  const location = getAircraftLocation(operator);

  if (location && location !== 'null') {
    return `${name} • ${location}`;
  }

  return name;
};

/**
 * Get the aircraft type from various possible data structures
 * @param {Object} aircraft - The aircraft object (can be from aircraftDetails or tail)
 * @returns {string} The aircraft type or '-' if not available
 */
export const getAircraftType = (aircraft) => {
  if (!aircraft) return '-';
  return aircraft?.aircraft_make_model?.aircraft_type || aircraft?.aircraft_type || '-';
};

/**
 * Get a property value from aircraft object with support for multiple fallback property names
 * @param {Object} aircraft - The aircraft object (can be from aircraftDetails or tail)
 * @param {string[]} properties - Array of property names to check in order
 * @param {string} [suffix] - Optional suffix to append to the value (e.g., ' nm', ' Passengers')
 * @returns {string} The property value with optional suffix, or '-' if not available
 */
export const getAircraftProperty = (aircraft, properties, suffix = '') => {
  if (!aircraft) return '-';

  for (const prop of properties) {
    const value = aircraft[prop];
    if (value !== null && value !== undefined && value !== '') {
      return suffix ? `${value}${suffix}` : String(value);
    }
  }

  return '-';
};

const API_BASE_URL = process.env.REACT_APP_API_URL;

export const getAircraftPhotos = (aircraft, isCarousel = false) => {
    if (!aircraft) {
      return isCarousel ? [TrustJetPlane] : `${window.location.origin}/images/bgplaceholdernew.jpg`;
    }

    // First priority: image_urls from API
    if (aircraft?.image_urls && aircraft?.image_urls.length > 0) {
      if(isCarousel)
      {
        return aircraft.image_urls.map(url => formatImagUrl(url));
      }
      return formatImagUrl(aircraft.image_urls[0]);
    }

    // Second priority: local aircraft image by make_model
    const makeModel = aircraft?.aircraft_make_model?.make_model || aircraft?.make_model;
    if (makeModel) {
      if (isCarousel) {
        return getAircraftCarouselImages(makeModel);
      }
      return getAircraftImage(makeModel);
    }

    return isCarousel ? [TrustJetPlane] : `${window.location.origin}/images/bgplaceholdernew.jpg`;
};

export const formatImagUrl = (path) => {
   if (path.startsWith('http://') || path.startsWith('https://')) {
     return path;
   }
   return `${API_BASE_URL}${path}`;
};
